import BookingGridBase from '../common/BookingGridBase';
import BookingCardExit from './BookingCardExit';
import BookingCardExitv2 from './BookingCardExitv2';
import ResourceLegend from '../common/ResourceLegend';
import { useEffect, useMemo, useState, memo } from 'react';
import useResource from '../../../../hooks/useResource';
import { toast } from 'react-toastify';
import { createResourceColorById } from '../../../../util/resourceColorPool';

function GridExit({
  bookings,
  rowCount,
  date,
  updateBooking,
  handleOpenModal,
  entertainmentId,
  capacity,
  onBookingShift,
  entertainment,
  onDragStart,
  onDragActivated,
  onDragEnd,
  availableShiftTimes,
  draggedBookingDuration,
  isShiftable,
  isLoadingShiftTimes,
  isBookingReadyForShift,
  shiftTimeOffsetMinutes = 0,
  resourceOffsetMap = {},
}) {
  const [exitResources, setExitResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getResourcesByTypeAndEntertainment } = useResource();

  useEffect(() => {
    const fetchResources = async () => {
      if (!entertainmentId) return;
      try {
        setLoading(true);
        const resources = await getResourcesByTypeAndEntertainment('escape-room', entertainmentId);
        setExitResources(resources);
      } catch (err) {
        console.error('Error fetching exit resources:', err);
        toast.error('Fehler beim Laden der Exit-Ressourcen');
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, [entertainmentId]);

  const calcAttendance = bookingsList => {
    const totalRooms = exitResources.length;

    let times = Array.from({ length: (24 - 9) * 2 + 1 }, (_, index) => {
      const hour = 9 + Math.floor(index / 2);
      const minute = index % 2 === 0 ? '00' : '30';
      const qualifiedTime = new Date(
        date.get('year'),
        date.get('month'),
        date.get('date'),
        hour,
        minute
      );

      let bookedRooms = 0;
      let blockedRooms = 0;
      for (const booking of bookingsList) {
        const { bookingType } = booking;
        const startKey = bookingType === 'RegiondoBooking' ? 'eventStart' : 'startTime';
        const endKey = bookingType === 'RegiondoBooking' ? 'eventEnd' : 'endTime';

        const compStart = new Date(booking[startKey]);
        const compEnd = new Date(booking[endKey]);

        if (compStart <= qualifiedTime && compEnd > qualifiedTime) {
          if (booking.status === 'blocked') {
            blockedRooms++;
          } else {
            bookedRooms++;
          }
        }
      }

      const availableRooms = totalRooms - bookedRooms - blockedRooms;

      return {
        isFull: index % 2 === 0,
        time: `${hour}:${minute}`,
        qualifiedTime,
        i: index,
        s0: bookedRooms,
        frees0: availableRooms,
      };
    });

    return times;
  };

  const getResourceOffset = () => 0;
  const resourceColorById = useMemo(() => createResourceColorById(exitResources), [exitResources]);

  return (
    <BookingGridBase
      bookings={bookings}
      onOpenBlocker={handleOpenModal}
      rowCount={rowCount}
      date={date}
      slotCapacity={exitResources.length}
      calcAttendance={calcAttendance}
      capacityData={capacity}
      availableShiftTimes={availableShiftTimes}
      draggedBookingDuration={draggedBookingDuration}
      isLoadingShiftTimes={isLoadingShiftTimes}
      shiftTimeOffsetMinutes={0}
      renderLegend={() => (
        <ResourceLegend
          resources={exitResources}
          loading={loading}
          resourceColorById={resourceColorById}
        />
      )}
      renderBookingCard={(booking, idx, rowHeight) =>
        booking.bookingType === 'InternalBooking' ? (
          <BookingCardExitv2
            key={booking._id}
            booking={booking}
            resourceColorById={resourceColorById}
            updateBookingState={updateBooking}
            handleOpenModal={handleOpenModal}
            renderAvailableSlots={false}
            rowHeight={rowHeight}
            onBookingShift={onBookingShift}
            entertainment={entertainment}
            onDragStart={onDragStart}
            onDragActivated={onDragActivated}
            onDragEnd={onDragEnd}
            availableShiftTimes={availableShiftTimes}
            isShiftable={isShiftable}
            isBookingReadyForShift={isBookingReadyForShift}
            resourceStartOffsetMinutes={getResourceOffset(booking)}
            openingGridOffset={capacity?.gridOffset || 0}
          />
        ) : (
          <BookingCardExit
            key={booking._id}
            booking={booking}
            renderAvailableSlots={false}
            resourceColorById={resourceColorById}
          />
        )
      }
      renderAvailableSlots={false}
    />
  );
}

export default memo(GridExit);
